import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Download } from 'lucide-react';
import Header from '../components/Header';

export default function ConnectChatGPTPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleDownloadIcon = () => {
    const link = document.createElement('a');
    link.href = '/Fittory.png';
    link.download = 'Fittory.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const steps = [
    { id: '1', image: '/assets/connect_chatgpt/1.png' },
    { id: '2', image: '/assets/connect_chatgpt/2.png' },
    { id: '3', image: '/assets/connect_chatgpt/3.png' },
    { id: '4', image: '/assets/connect_chatgpt/4.png' },
    { id: '5', image: '/assets/connect_chatgpt/5.png', hasDownload: true },
    { id: '6', image: '/assets/connect_chatgpt/6.png' },
    { id: '7', image: '/assets/connect_chatgpt/7.png' },
    { id: '8', images: ['/assets/connect_chatgpt/8a.png', '/assets/connect_chatgpt/8b.png'], hasOptions: true },
    { id: '9', image: '/assets/connect_chatgpt/9.png' },
    { id: '10', image: '/assets/connect_chatgpt/10.png' },
  ];

  return (
    <div
      className="min-h-screen bg-[#ffffeb]"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Header */}
      <Header
        leftContent={
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/profile')}
              className="lg:hidden p-2 hover:bg-[#2da877] border-2 border-[#077f5d]"
              style={{
                boxShadow: '2px 2px 0 #077f5d',
                transition: 'none'
              }}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <p
              className="text-left text-base font-bold uppercase"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '14px',
              }}
            >
              {t('connect_chatgpt.title')}
            </p>
          </div>
        }
        maxWidth="max-w-lg"
        hideProfileMenu
      />

      {/* Content */}
      <div className="max-w-lg lg:max-w-5xl mx-auto px-6 py-6 pb-24 lg:pb-8">
        {/* Introduction */}
        <div
          className="bg-white border-2 border-[#077f5d] p-4 lg:p-6 mb-6"
          style={{ boxShadow: '3px 3px 0 #077f5d' }}
        >
          <div className="flex items-center gap-2 text-[#077f5d]">
            <span className="text-xs lg:text-sm font-mono font-bold">
              {t('connect_chatgpt.requirement')}
            </span>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-6 lg:space-y-8">
          {steps.map((step) => (
            <div
              key={step.id}
              className="bg-white border-2 border-[#077f5d] overflow-hidden"
              style={{ boxShadow: '3px 3px 0 #077f5d' }}
            >
              {/* Step Header */}
              <div className="bg-[#dffbd0] border-b-2 border-[#077f5d] px-4 lg:px-6 py-3 lg:py-4 flex items-center gap-3 lg:gap-4">
                <div
                  className="w-8 h-8 lg:w-10 lg:h-10 bg-[#077f5d] border-2 border-[#055a42] flex items-center justify-center text-white text-xs lg:text-sm font-bold"
                  style={{ 
                    boxShadow: '2px 2px 0 #055a42',
                    fontFamily: "'Press Start 2P', monospace"
                  }}
                >
                  {step.id}
                </div>
                <h3 className="font-bold text-[#1a1a1a] font-mono text-sm lg:text-base">
                  {t(`connect_chatgpt.steps.${step.id}.title`)}
                </h3>
              </div>

              {/* Step Content */}
              <div className="p-4 lg:p-6">
                <p className="text-[#1a1a1a] font-mono text-sm lg:text-base mb-4 lg:mb-6">
                  {t(`connect_chatgpt.steps.${step.id}.desc`)}
                </p>

                {/* Step 5: Show structured input fields */}
                {step.id === '5' && (
                  <div className="bg-[#ffffeb] border-2 border-[#077f5d] p-3 lg:p-4 mb-4 lg:mb-6">
                    <div className="space-y-3 lg:space-y-4">
                      {/* Icon with download button */}
                      <div className="flex items-center gap-2">
                        <span className="text-[#077f5d] font-mono text-xs lg:text-sm font-bold min-w-[120px] lg:min-w-[160px]">Icon:</span>
                        <button
                          onClick={handleDownloadIcon}
                          className="flex items-center gap-2 bg-[#077f5d] text-white px-3 lg:px-4 py-1.5 lg:py-2 font-mono text-xs lg:text-sm font-bold border-2 border-[#055a42] hover:bg-[#066b4f]"
                          style={{ boxShadow: '2px 2px 0 #055a42' }}
                        >
                          <Download className="w-3 h-3 lg:w-4 lg:h-4" />
                          {t('connect_chatgpt.download_icon')}
                        </button>
                      </div>
                      {/* Name */}
                      <div className="flex items-center gap-2">
                        <span className="text-[#077f5d] font-mono text-xs lg:text-sm font-bold min-w-[120px] lg:min-w-[160px]">Name:</span>
                        <span className="text-[#1a1a1a] font-mono text-xs lg:text-sm">Fittory</span>
                      </div>
                      {/* MCP Server URL */}
                      <div className="flex items-center gap-2">
                        <span className="text-[#077f5d] font-mono text-xs lg:text-sm font-bold min-w-[120px] lg:min-w-[160px]">MCP Server URL:</span>
                        <span className="text-[#1a1a1a] font-mono text-xs lg:text-sm break-all">https://mcp.fittory.id</span>
                      </div>
                      {/* Authentication */}
                      <div className="flex items-center gap-2">
                        <span className="text-[#077f5d] font-mono text-xs lg:text-sm font-bold min-w-[120px] lg:min-w-[160px]">Authentication:</span>
                        <span className="text-[#1a1a1a] font-mono text-xs lg:text-sm">OAuth</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 8: Show options */}
                {step.hasOptions && (
                  <div className="bg-[#ffffeb] border-2 border-[#077f5d] p-3 lg:p-4 mb-4 lg:mb-6">
                    <div className="space-y-1 lg:space-y-2">
                      <p className="text-[#077f5d] font-mono text-xs lg:text-sm">
                        <span className="font-bold">a.</span> {t(`connect_chatgpt.steps.${step.id}.option_a`)}
                      </p>
                      <p className="text-[#077f5d] font-mono text-xs lg:text-sm">
                        <span className="font-bold">b.</span> {t(`connect_chatgpt.steps.${step.id}.option_b`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Screenshot(s) */}
                {step.images ? (
                  <div className="space-y-3 lg:space-y-4 lg:flex lg:flex-col lg:items-center">
                    {step.images.map((img, idx) => (
                      <div
                        key={idx}
                        className="border-2 border-[#077f5d] overflow-hidden lg:max-h-[300px] lg:w-fit"
                        style={{ boxShadow: '2px 2px 0 #077f5d' }}
                      >
                        <img
                          src={img}
                          alt={`Step ${step.id} option ${String.fromCharCode(97 + idx)}`}
                          className="w-full h-auto lg:h-full lg:w-auto lg:max-h-[296px] object-contain"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="lg:flex lg:justify-center">
                    <div
                      className="border-2 border-[#077f5d] overflow-hidden lg:max-h-[400px] lg:w-fit"
                      style={{ boxShadow: '2px 2px 0 #077f5d' }}
                    >
                      <img
                        src={step.image}
                        alt={`Step ${step.id}`}
                        className="w-full h-auto lg:h-full lg:w-auto lg:max-h-[396px] object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
